import React, { useState, useCallback, forwardRef } from "react";

export type ImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  fill?: boolean;
  unoptimized?: boolean;
  priority?: boolean;
  quality?: number;
  sizes?: string;
};

/**
 * Optimized Image component for Vite-based projects.
 * Provides lazy loading, async decoding, and responsive image best practices.
 *
 * Usage:
 * - For priority images (above the fold): <Image src="..." width={800} height={600} priority />
 * - For lazy-loaded images: <Image src="..." width={800} height={600} loading="lazy" />
 * - For responsive images: <Image src="..." fill sizes="(max-width: 768px) 100vw, 50vw" />
 */
const Image = forwardRef<HTMLImageElement, ImageProps>(function Image({
  fill,
  style,
  priority,
  unoptimized,
  sizes,
  loading,
  decoding = "async",
  fetchPriority,
  onLoad,
  className,
  alt = "",
  width,
  height,
  src,
  ...props
}, ref) {
  const [isLoaded, setIsLoaded] = useState(false);

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      setIsLoaded(true);
      onLoad?.(e);
    },
    [onLoad]
  );

  // Determine loading strategy
  const loadingStrategy = priority ? "eager" : loading || "lazy";

  // Determine fetchPriority
  const fetchPriorityValue = priority
    ? "high"
    : fetchPriority || (loadingStrategy === "lazy" ? "low" : "auto");

  const mergedStyle: React.CSSProperties = fill
    ? {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        opacity: isLoaded ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        ...(style as React.CSSProperties | undefined),
      }
    : {
        opacity: isLoaded ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        ...(style as React.CSSProperties | undefined),
      };

  // For non-priority images that are below the fold, add lazy loading
  const imgProps = {
    ...props,
    src,
    alt,
    width: fill ? undefined : width,
    height: fill ? undefined : height,
    loading: loadingStrategy as "eager" | "lazy",
    decoding: decoding as "async" | "sync" | "auto",
    fetchPriority: fetchPriorityValue as "high" | "low" | "auto",
    draggable: false,
    onLoad: handleLoad,
  };

  // If using fill mode, wrap in a container
  if (fill) {
    return (
      <img
        ref={ref}
        {...imgProps}
        style={mergedStyle}
        data-unoptimized={unoptimized ? "true" : undefined}
        data-loaded={isLoaded ? "true" : undefined}
        className={className}
        sizes={sizes}
      />
    );
  }

  return (
    <img
      ref={ref}
      {...imgProps}
      style={mergedStyle}
      data-unoptimized={unoptimized ? "true" : undefined}
      data-loaded={isLoaded ? "true" : undefined}
      className={className}
      sizes={sizes}
    />
  );
});

export default Image;
